import { Navigate, useRoutes } from "react-router-dom";
// auth
import AuthGuard from "../auth/AuthGuard";
import GuestGuard from "../auth/GuestGuard";
// layouts
import CompactLayout from "../layouts/compact";
import DashboardLayout from "../layouts/dashboard";
// config
import { useEffect } from "react";
import { useSnackbar } from "../components/snackbar";
import { PATH_AFTER_LOGIN } from "../config-global";
import LandingPage from "../pages/LandingPage";
import LoginPage from "../pages/LoginPage";
import Page404 from "../pages/Page404";
import Profile from "../pages/general/Profile";
import { fetchCategories } from "../redux/slices/category";
import { useDispatch } from "../redux/store";
import {
  AssignPrinter,
  BlogList,
  CalendarOrders,
  CartModalPage,
  CategoryList,
  CateringFormData,
  CateringList,
  ContactList,
  CreateBlog,
  CreateCategory,
  CreateCatering,
  CreateContact,
  CreateFaq,
  CreateGallery,
  CreateNotice,
  CreateOrder,
  CreatePage,
  CreateProduct,
  CreateRider,
  CreateTag,
  CreateUser,
  FaqList,
  GalleryList,
  NoticeList,
  Notification,
  OrderKanban,
  OrderList,
  PageList,
  POSPage,
  PromoCodeList,
  PromoCodeUsagePage,
  ProductList,
  RiderList,
  SettingsPage,
  SitemapPage,
  StockHistories,
  TagsList,
  UserList,
  ViewEditBlog,
  ViewEditCategory,
  ViewEditCatering,
  ViewEditGallery,
  ViewEditNotice,
  ViewEditOrder,
  ViewEditPage,
  ViewEditProduct,
  ViewEditTag,
  ActivityLogList,
} from "./elements";
//

// ----------------------------------------------------------------------

export default function Router() {
  const dispatch = useDispatch();
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    dispatch(fetchCategories({ enqueueSnackbar }));
  }, [dispatch, enqueueSnackbar]);

  return useRoutes([
    {
      path: "/",
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN} replace />,
          index: true,
        },
        {
          path: "login",
          element: (
            <GuestGuard>
              <LoginPage />
            </GuestGuard>
          ),
        },
        {
          path: "pos",
          element: (
            <AuthGuard>
              <POSPage />
            </AuthGuard>
          ),
        },
      ],
    },
    {
      path: "/",
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN} replace />,
          index: true,
        },
        // {
        //   path: "privacy-policy",
        //   element: (
        //     <GuestGuard>
        //       <ViewPrivacyPolicy />
        //     </GuestGuard>
        //   ),
        // },
      ],
    },

    {
      path: "/admin",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          element: <Navigate to={PATH_AFTER_LOGIN} replace />,
          index: true,
        },
        { path: "dashboard", element: <LandingPage /> },
        {
          path: "profile",
          element: <Profile />,
        },
        {
          path: "notification",
          element: <Notification />,
        },
        {
          path: "stock-histories",
          element: <StockHistories />,
        },

        // TODO: contents
        // TODO: blogs
        {
          path: "contents/blogs",
          children: [
            {
              element: <Navigate to="/admin/contents/blogs/" replace />,
              index: true,
            },
            { path: "list", element: <BlogList /> },
            { path: "add", element: <CreateBlog /> },
            {
              path: "view/:slug",
              element: <ViewEditBlog isEdit={false} isView={true} />,
            },
            { path: "edit/:slug", element: <ViewEditBlog isEdit={true} /> },
          ],
        },
        // TODO: caterings
        {
          path: "contents/caterings",
          children: [
            {
              element: <Navigate to="/admin/contents/caterings/" replace />,
              index: true,
            },
            { path: "list", element: <CateringList /> },
            { path: "add", element: <CreateCatering /> },
            {
              path: "view/:slug",
              element: <ViewEditCatering isEdit={false} isView={true} />,
            },
            { path: "edit/:slug", element: <ViewEditCatering isEdit={true} /> },
            {
              path: "catering-form-data",
              element: <CateringFormData />,
            },
          ],
        },
        // tags
        {
          path: "contents/tags",
          children: [
            {
              element: <Navigate to="/admin/contents/tags/" replace />,
              index: true,
            },
            { path: "list", element: <TagsList /> },
            { path: "add", element: <CreateTag /> },
            { path: "edit/:id", element: <ViewEditTag isEdit={true} /> },
          ],
        },

        // TODO: Entries section

        // TODO: galleries
        {
          path: "entries/galleries",
          children: [
            {
              element: <Navigate to="/admin/entries/galleries/" replace />,
              index: true,
            },
            { path: "list", element: <GalleryList /> },
            { path: "add", element: <CreateGallery /> },
            {
              path: "view/:slug",
              element: <ViewEditGallery isEdit={false} view={true} />,
            },
            { path: "edit/:slug", element: <ViewEditGallery isEdit={true} /> },
          ],
        },

        // TODO: categories
        {
          path: "entries/categories",
          children: [
            {
              element: <Navigate to="/admin/entries/categories/" replace />,
              index: true,
            },
            { path: "list", element: <CategoryList /> },
            { path: "add", element: <CreateCategory /> },
            {
              path: "view/:slug",
              element: <ViewEditCategory isEdit={false} view={true} />,
            },
            { path: "edit/:slug", element: <ViewEditCategory isEdit={true} /> },
            {
              path: "assign-printer",
              element: <AssignPrinter />,
            },
          ],
        },

        // TODO: meta pages
        {
          path: "meta/pages",
          children: [
            {
              element: <Navigate to="/admin/meta/pages/" replace />,
              index: true,
            },
            { path: "list", element: <PageList /> },
            { path: "add", element: <CreatePage /> },
            {
              path: "view/:slug",
              element: <ViewEditPage isEdit={false} isView={true} />,
            },
            { path: "edit/:slug", element: <ViewEditPage isEdit={true} /> },
          ],
        },

        // TODO: meta sitemap
        {
          path: "meta/sitemap",
          element: <SitemapPage />,
        },

        // TODO: products
        {
          path: "products",
          children: [
            {
              element: <Navigate to="/admin/products/" replace />,
              index: true,
            },
            { path: "list", element: <ProductList /> },
            { path: "add", element: <CreateProduct /> },
            {
              path: "view/:slug",
              element: <ViewEditProduct isEdit={false} view={true} />,
            },
            { path: "edit/:slug", element: <ViewEditProduct isEdit={true} /> },
          ],
        },
        // TODO: promo codes
        {
          path: "promo-codes",
          children: [
            {
              element: <Navigate to="/admin/promo-codes/list" replace />,
              index: true,
            },
            { path: "list", element: <PromoCodeList /> },
            { path: "usage/:code", element: <PromoCodeUsagePage /> },
          ],
        },

        // TODO: orders
        {
          path: "orders",
          children: [
            {
              element: <Navigate to="/admin/orders/" replace />,
              index: true,
            },
            { path: "list", element: <OrderList /> },
            { path: "calendar", element: <CalendarOrders /> },
            { path: "add", element: <CreateOrder /> },
            {
              path: "view/:slug",
              element: <ViewEditOrder isEdit={false} view={true} />,
            },
            { path: "edit/:slug", element: <ViewEditOrder isEdit={true} /> },
          ],
        },
        // TODO: crm orders
        {
          path: "crmorder",
          children: [
            {
              element: <Navigate to="/admin/crmorder/" replace />,
              index: true,
            },
            { path: "list", element: <OrderKanban /> },
            { path: "add", element: <CreateOrder /> },
            {
              path: "view/:slug",
              element: <ViewEditOrder isEdit={false} view={true} />,
            },
            { path: "edit/:slug", element: <ViewEditOrder isEdit={true} /> },
          ],
        },

        // TODO: contacts
        {
          path: "contacts",
          children: [
            {
              element: <Navigate to="/admin/contacts/" replace />,
              index: true,
            },
            { path: "list", element: <ContactList /> },
            { path: "add", element: <CreateContact /> },
          ],
        },

        // TODO: users
        {
          path: "users",
          children: [
            {
              element: <Navigate to="/admin/users/" replace />,
              index: true,
            },
            { path: "list", element: <UserList /> },
            { path: "add", element: <CreateUser /> },
          ],
        },
        // TODO: riders
        {
          path: "riders",
          children: [
            {
              element: <Navigate to="/admin/riders/" replace />,
              index: true,
            },
            { path: "list", element: <RiderList /> },
            { path: "add", element: <CreateRider /> },
          ],
        },

        // TODO: management
        {
          path: "manage/faqs",
          children: [
            {
              element: <Navigate to="/admin/manage/faqs/" replace />,
              index: true,
            },
            { path: "list", element: <FaqList /> },
            { path: "add", element: <CreateFaq /> },
          ],
        },

        // TODO: notices
        {
          path: "notices",
          children: [
            {
              element: <Navigate to="/admin/notices/" replace />,
              index: true,
            },
            { path: "list", element: <NoticeList /> },
            { path: "add", element: <CreateNotice /> },
            {
              path: "view/:slug",
              element: <ViewEditNotice isEdit={false} isView={true} />,
            },
            { path: "edit/:slug", element: <ViewEditNotice isEdit={true} /> },
          ],
        },

        // activity logs
        {
          path: "activity-logs",
          element: <ActivityLogList />,
        },

        // settings
        {
          path: "settings",
          element: <SettingsPage />,
        },

        // cart popup
        {
          path: "cart-modal",
          element: <CartModalPage />,
        },
      ],
    },
    // TODO:Rider
    {
      path: "rider",
      element: (
        <AuthGuard>
          <DashboardLayout />
        </AuthGuard>
      ),
      children: [
        {
          path: "profile",
          element: <Profile />,
        },
        {
          path: "crmorder/list",
          element: <OrderKanban />,
        },
      ],
    },

    {
      element: <CompactLayout />,
      children: [{ path: "404", element: <Page404 /> }],
    },

    {
      path: "*",
      element: <Navigate to="/404" replace />,
    },
  ]);
}
